import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { Play, MapPin, Calendar, Users, CaretLeft, CaretRight, Printer, SignIn, IdentificationBadge } from '@phosphor-icons/react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ContactInYourCountry from '../components/ContactInYourCountry';
import CountryModal from '../components/CountryModal';
import useHomepageData from '../hooks/useHomepageData';
import {
  dayLabel,
  dayTimeSpan,
  formatDuration,
  formatTimeRange,
  groupScheduleByDay,
} from '../lib/schedule';
import useRevealOnScroll from '../hooks/useRevealOnScroll';

const countryCodes = [
  { name: 'España', code: '+34' }, { name: 'México', code: '+52' },
  { name: 'Argentina', code: '+54' }, { name: 'Colombia', code: '+57' },
  { name: 'Chile', code: '+56' }, { name: 'Perú', code: '+51' },
  { name: 'Venezuela', code: '+58' }, { name: 'Ecuador', code: '+593' },
  { name: 'Guatemala', code: '+502' }, { name: 'Cuba', code: '+53' },
  { name: 'Bolivia', code: '+591' }, { name: 'República Dominicana', code: '+1-809' },
  { name: 'Honduras', code: '+504' }, { name: 'Paraguay', code: '+595' },
  { name: 'El Salvador', code: '+503' }, { name: 'Nicaragua', code: '+505' },
  { name: 'Costa Rica', code: '+506' }, { name: 'Panamá', code: '+507' },
  { name: 'Uruguay', code: '+598' }, { name: 'Estados Unidos', code: '+1' },
  { name: 'Bangladesh', code: '+880' },
];

// Valor centinela para "Otro país" — permite ingresar manualmente un país
// que no esté en la lista, en vez de tener que agregar cada país uno por uno.
const OTHER_COUNTRY_VALUE = '__otro__';

// ─── Utilidades YouTube ───────────────────────────────────────────────────────
const toEmbedUrl = (url) => {
  if (!url) return '';
  if (url.includes('/embed/')) return url;
  const match = url.match(/(?:youtu\.be\/|watch\?v=|shorts\/)([^?&]+)/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return url;
};

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:embed\/|watch\?v=|youtu\.be\/|shorts\/)([^?&]+)/);
  return match ? match[1] : null;
};

// hqdefault.jpg (480×360) está disponible en TODOS los videos de YouTube.
// maxresdefault.jpg a veces no existe y YouTube devuelve una imagen gris placeholder.
const getYoutubeThumbnail = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// ─── Carrusel genérico ────────────────────────────────────────────────────────
const Carousel = ({ images, className = '' }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((idx) => setCurrent((idx + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    timerRef.current = setInterval(() => setCurrent(c => (c + 1) % images.length), 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, images.length]);

  const handleManual = (dir) => {
    setPaused(true);
    goTo(current + dir);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPaused(false), 6000);
  };

  if (!images.length) return null;

  return (
    <div className={`relative overflow-hidden rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.10)] ${className}`}>
      <div className="relative w-full h-full bg-[#111]">
        {images.map((img, idx) => (
          <div key={img.id || idx}
            className={`absolute inset-0 transition-opacity duration-700 ${idx === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
            <img src={img.url} alt={img.caption || `Foto ${idx + 1}`}
              className="w-full h-full object-cover" loading="lazy" decoding="async" />
            {img.caption && (
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-sm font-medium">{img.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <>
          <button type="button" aria-label="Imagen anterior" onClick={() => handleManual(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all hover:scale-110">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button type="button" aria-label="Imagen siguiente" onClick={() => handleManual(1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all hover:scale-110">
            <CaretRight size={16} weight="bold" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1">
            {images.map((_, idx) => (
              <button type="button" aria-label={`Ver imagen ${idx + 1}`} key={idx} onClick={() => { setPaused(true); goTo(idx); }}
                className={`h-1.5 rounded-full transition-all ${idx === current ? 'bg-white w-4' : 'bg-white/50 w-1.5'}`} />
            ))}
          </div>
          <div className="absolute top-2 right-2 z-20 bg-black/40 text-white text-xs px-2 py-0.5 rounded-full">
            {current + 1}/{images.length}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Componente de Video YouTube ──────────────────────────────────────────────
// customCover: imagen personalizada subida por el admin (tiene prioridad)
// Si no hay customCover, usa hqdefault.jpg de YouTube (siempre disponible)
const YouTubeVideo = ({ url, title, customCover }) => {
  const [playing, setPlaying] = useState(false);
  const embedUrl = toEmbedUrl(url);

  // Prioridad: portada personalizada > thumbnail YouTube (hqdefault, siempre disponible)
  const thumb = customCover || getYoutubeThumbnail(url);

  if (!embedUrl) return null;

  return (
    <div className="relative aspect-video w-full rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.10)] bg-black">
      {!playing && thumb ? (
        <div className="absolute inset-0 cursor-pointer group" onClick={() => setPlaying(true)}>
          <img
            src={thumb}
            alt={title || 'Video'}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="w-16 h-16 bg-[#FF0000] rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
              <Play size={28} weight="fill" className="text-white ml-1" />
            </div>
          </div>
        </div>
      ) : (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`${embedUrl}?autoplay=1&rel=0`}
          title={title || 'Video'}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

// ─── HomePage ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const {
    videos,
    faqs,
    speakers,
    schedule,
    scheduleDays,
    participatingCountries,
    galleryImages,
    aboutImages,
    formFields,
    content,
    loading: contentLoading,
    loadError,
    refresh: fetchData,
  } = useHomepageData();

  const revealKey = [
    Object.keys(content).length,
    videos.length,
    speakers.length,
    schedule.length,
    scheduleDays.length,
    participatingCountries.length,
    galleryImages.length,
    faqs.length,
  ].join('-');
  useRevealOnScroll(revealKey);

  // El programa se arma agrupando las actividades por día; los talleres que
  // comparten hora dentro de un día se muestran como simultáneos.
  const scheduleByDay = useMemo(
    () => groupScheduleByDay(scheduleDays, schedule).filter((group) => group.events.length > 0),
    [scheduleDays, schedule],
  );

  // Imprime solo el programa. En vez de posicionar la sección (frágil por los
  // ancestros con position: relative), se marca la rama del DOM que lleva hasta
  // ella: el CSS de impresión oculta a todos sus hermanos, así el programa
  // queda solo en el flujo y no se generan páginas en blanco.
  const handlePrintSchedule = useCallback(() => {
    const target = document.getElementById('programa-imprimible');
    if (!target) return;

    const marked = [];
    let node = target;
    while (node && node.parentElement) {
      node.classList.add('programa-rama-impresion');
      node.parentElement.classList.add('programa-ancestro-impresion');
      marked.push(node, node.parentElement);
      node = node.parentElement;
    }
    document.body.classList.add('imprimiendo-programa');

    const cleanup = () => {
      document.body.classList.remove('imprimiendo-programa');
      marked.forEach((element) => {
        element.classList.remove('programa-rama-impresion', 'programa-ancestro-impresion');
      });
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
    // Safari en iOS no siempre dispara afterprint.
    setTimeout(cleanup, 2000);
  }, []);

  const [selectedCountryModal, setSelectedCountryModal] = useState(null);
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [formData, setFormData] = useState({ country: '', phone: '', email: '', instagram: '', church: '', city: '' });
  const [submitting, setSubmitting] = useState(false);
  const [customFields, setCustomFields] = useState({});

  const handleCountryChange = (countryName) => {
    if (countryName === OTHER_COUNTRY_VALUE) {
      setSelectedCountry(OTHER_COUNTRY_VALUE);
      setPhoneCode('');
      setFormData(prev => ({ ...prev, country: '' }));
      return;
    }
    const country = countryCodes.find(c => c.name === countryName);
    if (country) {
      setSelectedCountry(countryName);
      setPhoneCode(country.code);
      setFormData(prev => ({ ...prev, country: countryName }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCountry === OTHER_COUNTRY_VALUE && !formData.country.trim()) {
      toast.error('Escribe el nombre de tu país');
      return;
    }
    setSubmitting(true);
    try {
      const normalizedEmail = formData.email.trim().toLowerCase();
      const { error } = await supabase.from('registrations').insert({
        ...formData,
        email: normalizedEmail,
        phone: `${phoneCode}${formData.phone}`,
        custom_fields: customFields,
      });
      if (error) throw error;
      toast.success('¡Registro exitoso! Te contactaremos pronto.');
      setFormData({ country: '', phone: '', email: '', instagram: '', church: '', city: '' });
      setCustomFields({});
      setSelectedCountry('');
      setPhoneCode('');
    } catch (error) {
      if (error?.code === '23505') {
        toast.error('Usuario ya registrado');
      } else {
        toast.error('Error al registrarse. Intenta nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hero = content.hero || { title: 'Conéctate CCI' };
  // El super admin abre o cierra la pre-inscripción desde Portal del evento.
  // Sin configuración guardada, el formulario sigue abierto como siempre.
  const registrationSettings = content.registration || {};
  const registrationOpen = registrationSettings.open !== false;
  const goToRegistration = () => document.getElementById('registro')?.scrollIntoView({ behavior: 'smooth' });
  const goToPortal = () => { window.location.href = '/portal'; };
  const ultimo = content.ultimo || {};
  const topLogoUrl = hero.top_logo_url || '';
  const topLogo2Url = hero.top_logo2_url || '';
  const bgImageUrl = hero.bg_image_url || '';
  const galleryTitle = ultimo.gallery_title || 'Galería de Fotos';

  return (
    <div className="home-page min-h-screen bg-[#F5F5F7]">
      {contentLoading && <div className="homepage-progress" role="progressbar" aria-label="Actualizando contenido" />}

      {/* Banner de error de carga */}
      {loadError && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-center text-sm text-amber-800">
          Algunos contenidos no pudieron cargarse.{' '}
          <button onClick={() => fetchData()}
            className="underline font-semibold hover:text-[#F04A22]">Reintentar</button>
        </div>
      )}

      {/* ── Header ── */}
      <header className="home-header backdrop-blur-xl bg-white/80 border-b border-black/5 sticky top-0 z-50" data-testid="main-header">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {topLogoUrl && <img src={topLogoUrl} alt="Logo principal" className="h-12 md:h-14 object-contain" />}
            {topLogo2Url && (
              <>
                <div className="w-px h-8 bg-[#E5E5E5]" />
                <img src={topLogo2Url} alt="Logo secundario" className="h-12 md:h-14 object-contain" />
              </>
            )}
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Acerca</a>
            <a href="#objetivo" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Objetivo</a>
            <a href="#proposito" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Propósito</a>
            <a href="#videos" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Videos</a>
            {registrationOpen && (
              <a href="#registro" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Registro</a>
            )}
            <a href="#faq" className="text-sm font-medium hover:text-[#F04A22] transition-colors">FAQ</a>
            <a href="/portal" className="text-sm font-medium hover:text-[#F04A22] transition-colors">Portal participantes</a>
          </nav>
          {/* En móvil solo se muestra el botón principal compacto */}
          <div className="flex items-center gap-2">
            <Button className="bg-[#F04A22] hover:bg-[#D03A18] text-white px-3 md:px-6 py-2 rounded-md font-semibold text-xs md:text-sm"
              onClick={registrationOpen ? goToRegistration : goToPortal}>
              {registrationOpen ? (
                <>
                  <span className="hidden sm:inline">Pre-Inscripción</span>
                  <span className="sm:hidden">Registro</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Portal del participante</span>
                  <span className="sm:hidden">Ingresar</span>
                </>
              )}
            </Button>
            <div className="hidden sm:block">
              <ContactInYourCountry />
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="home-hero relative min-h-[90vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {bgImageUrl && (
          <div className="absolute inset-0 z-0" style={{ backgroundImage: `url(${bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15 }} />
        )}
        <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {hero.title_type === 'image' && hero.title_image_url ? (
                <img data-reveal="hero" src={hero.title_image_url} alt="Título del evento" className="max-w-sm mb-6" />
              ) : (
                <h1 data-reveal="hero" className="text-5xl sm:text-6xl md:text-7xl tracking-tighter font-black leading-none mb-6">
                  {hero.title}
                </h1>
              )}
              {hero.subtitle && (
                <p data-reveal="up" style={{ '--reveal-delay': '110ms' }} className="text-base md:text-lg leading-relaxed text-[#111111]/80 mb-8 max-w-2xl [&_strong]:font-bold [&_em]:italic [&_p]:mb-1 [&_br]:block">
                  {hero.subtitle?.includes('<') 
                    ? <span dangerouslySetInnerHTML={{ __html: hero.subtitle }} />
                    : hero.subtitle
                  }
                </p>
              )}
              <div data-reveal="up" style={{ '--reveal-delay': '180ms' }} className="flex flex-wrap gap-6 items-center mb-8">
                <div className="flex items-center gap-2"><MapPin size={24} weight="bold" className="text-[#F04A22]" /><span className="font-semibold">Barcelona, España</span></div>
                {hero.date && <div className="flex items-center gap-2"><Calendar size={24} weight="bold" className="text-[#F04A22]" /><span className="font-semibold">{hero.date}</span></div>}
                <div className="flex items-center gap-2"><Users size={24} weight="bold" className="text-[#F04A22]" /><span className="font-semibold">Conéctate Europa</span></div>
              </div>
              <Button data-reveal="up" style={{ '--reveal-delay': '240ms' }} className="bg-[#F04A22] hover:bg-[#D03A18] text-white px-8 py-6 text-lg rounded-md font-bold"
                onClick={registrationOpen ? goToRegistration : goToPortal}>
                {registrationOpen ? 'Regístrate Ahora' : 'Ingresar al portal'}
              </Button>
            </div>
            {hero.partner_logo_url && (
              <div data-reveal="pop" style={{ '--reveal-delay': '140ms' }} className="hero-emblem hidden lg:flex items-center justify-center">
                <img src={hero.partner_logo_url} alt="Red Global CCI" className="max-w-xs w-full drop-shadow-2xl" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          {content.about?.title && (
            <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-12">{content.about.title}</h2>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {content.about?.description && (
              <div data-reveal="left" className="text-base md:text-lg leading-relaxed text-[#111111]/80 [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: content.about.description }} />
            )}
            <div data-reveal="right" className="w-full h-80">
              {aboutImages.length > 0 ? (
                <Carousel images={aboutImages} className="h-80" />
              ) : (
                <img src="https://images.pexels.com/photos/1388030/pexels-photo-1388030.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Barcelona" className="w-full h-full object-cover rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.04)]" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Países participantes ── */}
      {participatingCountries.length > 0 && (
        <section className="py-16 md:py-20 bg-white px-6 md:px-12 lg:px-24" data-testid="participating-countries-section">
          <div className="max-w-7xl mx-auto">
            <h2 data-reveal="up"
              className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold text-center mb-3">Países Participantes</h2>
            <p data-reveal="up" style={{ '--reveal-delay': '90ms' }}
              className="text-center text-[#737373] mb-12 max-w-2xl mx-auto">Conéctate reúne líderes de toda América y Europa</p>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
              {participatingCountries.map((country, idx) => (
                <div key={country.id} data-reveal="pop" style={{ '--reveal-delay': `${Math.min(idx, 9) * 65}ms` }}
                  onClick={() => { setSelectedCountryModal(country); setCountryModalOpen(true); }}
                  className="flex flex-col items-center gap-2 group cursor-pointer">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.08)] group-hover:shadow-[0_8px_24px_rgba(240,74,34,0.2)] transition-shadow border-4 border-white">
                    <img src={`https://flagcdn.com/w160/${country.flag_code.toLowerCase()}.png`} alt={`Bandera de ${country.name}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-[#111111]/70 group-hover:text-[#F04A22] transition-colors text-center">{country.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {selectedCountryModal && <CountryModal country={selectedCountryModal} open={countryModalOpen} onOpenChange={setCountryModalOpen} />}

      {/* ── Objetivo ── */}
      {content.objetivo?.title && (
        <section id="objetivo" className="py-24 md:py-32 bg-white px-6 md:px-12 lg:px-24" data-testid="objetivo-section">
          <div className="max-w-7xl mx-auto">
            <div className={`flex flex-col ${content.objetivo?.image_url ? 'lg:flex-row items-center gap-16' : ''}`}>
              <div data-reveal="left" className={content.objetivo?.image_url ? 'lg:w-1/2' : 'max-w-4xl'}>
                <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-8">{content.objetivo.title}</h2>
                <div className="text-base md:text-lg leading-relaxed text-[#111111]/80 [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: content.objetivo.description }} />
              </div>
              {content.objetivo?.image_url && (
                <div data-reveal="right" className="lg:w-1/2 flex-shrink-0 mt-10 lg:mt-0">
                  <img src={content.objetivo.image_url} alt="Objetivo"
                    className="w-full rounded-2xl object-cover shadow-lg max-h-[480px]" loading="lazy" decoding="async" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      {content.proposito?.title && (
        <section id="proposito" className="py-24 md:py-32 bg-[#FDFCFB] px-6 md:px-12 lg:px-24" data-testid="proposito-section">
          <div className="max-w-7xl mx-auto">
            <div className={`flex flex-col ${content.proposito?.image_url ? 'lg:flex-row-reverse items-center gap-16' : ''}`}>
              <div data-reveal="right" className={content.proposito?.image_url ? 'lg:w-1/2' : 'max-w-4xl'}>
                <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-8">{content.proposito.title}</h2>
                <div className="text-base md:text-lg leading-relaxed text-[#111111]/80 [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: content.proposito.description }} />
              </div>
              {content.proposito?.image_url && (
                <div data-reveal="left" className="lg:w-1/2 flex-shrink-0 mt-10 lg:mt-0">
                  <img src={content.proposito.image_url} alt="Propósito"
                    className="w-full rounded-2xl object-cover shadow-lg max-h-[480px]" loading="lazy" decoding="async" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Último Conéctate ── */}
      {(ultimo.video_url || galleryImages.length > 0) && (
        <section className="py-24 md:py-32 bg-white px-6 md:px-12 lg:px-24" data-testid="ultimo-conectate-section">
          <div className="max-w-7xl mx-auto">
            {ultimo.title && <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-4">{ultimo.title}</h2>}
            {ultimo.description && <div className="text-base md:text-lg leading-relaxed text-[#111111]/80 mb-12 [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: ultimo.description }} />}

            {/* Video con portada personalizada o thumbnail automático de YouTube */}
            {ultimo.video_url && (
              <div data-reveal="up" className="mb-12 max-w-2xl mx-auto">
                <YouTubeVideo
                  url={ultimo.video_url}
                  title={ultimo.title}
                  customCover={ultimo.cover_image_url || null}
                />
              </div>
            )}

            {/* Galería con título editable */}
            {galleryImages.length > 0 && (
              <div data-reveal="up" className="max-w-2xl mx-auto">
                <h3 className="text-xl font-bold mb-4">{galleryTitle}</h3>
                <Carousel images={galleryImages} className="aspect-video" />
                {galleryImages.length > 1 && (
                  <p className="text-center text-xs text-[#737373] mt-3">{galleryImages.length} fotografías</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Speakers ── */}
      {speakers.length > 0 && (
        <section id="oradores" className="py-24 md:py-32 bg-white px-6 md:px-12 lg:px-24" data-testid="speakers-section">
          <div className="max-w-7xl mx-auto">
            <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-12">Oradores</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {speakers.map((speaker, idx) => (
                <div key={speaker.id} data-reveal="up" style={{ '--reveal-delay': `${Math.min(idx, 5) * 80}ms` }} className="bg-[#FDFCFB] border border-black/10 rounded-lg p-6">
                  {speaker.photo_url && <img src={speaker.photo_url} alt={speaker.name} className="w-24 h-24 rounded-full mx-auto mb-4 object-cover" />}
                  <h3 className="font-bold text-xl text-center mb-2">{speaker.name}</h3>
                  <p className="text-[#F04A22] text-center text-sm font-semibold mb-3">{speaker.title}</p>
                  <div
                    className="text-[#111111]/80 text-sm leading-relaxed [&_h1]:text-xl [&_h1]:font-black [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:my-1 [&_p]:mb-2"
                    dangerouslySetInnerHTML={{ __html: speaker.bio }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Schedule ── */}
      {scheduleByDay.length > 0 && (
        <section id="programa" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFCFB]" data-testid="schedule-section">
          <div id="programa-imprimible" className="max-w-5xl mx-auto">
            <div data-reveal="up" className="programa-encabezado-web mb-12 flex flex-wrap items-end justify-between gap-6">
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold">Programa del Evento</h2>
                <p className="mt-3 text-[#111111]/70">
                  {scheduleByDay.length === 1
                    ? '1 día de actividades'
                    : `${scheduleByDay.length} días de actividades`}
                </p>
              </div>
              <button
                type="button"
                onClick={handlePrintSchedule}
                className="programa-no-imprimir inline-flex items-center gap-2 rounded-full border border-[#F04A22] px-5 py-2.5 text-sm font-semibold text-[#F04A22] transition-colors hover:bg-[#F04A22] hover:text-white"
                data-testid="schedule-print-button"
              >
                <Printer size={18} weight="bold" />
                Imprimir / Guardar PDF
              </button>
            </div>

            <div className="programa-encabezado-impresion">
              <p className="programa-encabezado-titulo">Conéctate CCI · Programa del Evento</p>
            </div>

            <div className="space-y-16">
              {scheduleByDay.map((group) => (
                <div key={group.day.id} className="programa-dia">
                  <div data-reveal="up" className="mb-8 border-b border-[#F04A22]/30 pb-4">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-[#111111]">
                      {dayLabel(group.day)}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                      {group.day.subtitle && <span>{group.day.subtitle}</span>}
                      {dayTimeSpan(group.events) && (
                        <span className="font-semibold text-[#F04A22] tabular-nums">
                          {dayTimeSpan(group.events)}
                        </span>
                      )}
                      <span>
                        {group.events.length === 1
                          ? '1 actividad'
                          : `${group.events.length} actividades`}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {group.blocks.map((block, idx) =>
                      block.type === 'activity' ? (
                        <article
                          key={block.key}
                          data-reveal="up"
                          style={{ '--reveal-delay': `${Math.min(idx, 5) * 70}ms` }}
                          className="programa-actividad grid grid-cols-1 gap-4 rounded-xl border border-black/10 bg-white p-6 md:grid-cols-[170px_1fr] md:gap-8 md:p-8"
                        >
                          <div className="md:border-r md:border-black/10 md:pr-6">
                            <p className="text-lg font-bold text-[#F04A22] tabular-nums">
                              {formatTimeRange(block.event.start_time, block.event.duration_minutes)}
                            </p>
                            {formatDuration(block.event.duration_minutes) && (
                              <p className="mt-1 text-sm text-[#737373]">
                                Duración: {formatDuration(block.event.duration_minutes)}
                              </p>
                            )}
                          </div>
                          <div>
                            <h4 className="text-xl font-bold">{block.event.title}</h4>
                            {block.event.description && (
                              <p className="mt-2 text-[#111111]/80">{block.event.description}</p>
                            )}
                            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                              {block.event.responsible && (
                                <span className="font-semibold text-[#F04A22]">
                                  Responsable: {block.event.responsible}
                                </span>
                              )}
                              {block.event.location && (
                                <span className="text-[#737373]">Lugar: {block.event.location}</span>
                              )}
                            </div>
                          </div>
                        </article>
                      ) : (
                        <section
                          key={block.key}
                          data-reveal="up"
                          style={{ '--reveal-delay': `${Math.min(idx, 5) * 70}ms` }}
                          className="programa-talleres rounded-xl border border-[#F04A22]/30 bg-[#F04A22]/[0.04] p-6 md:p-8"
                        >
                          <header className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <p className="text-lg font-bold text-[#F04A22] tabular-nums">
                              {formatTimeRange(block.startTime, block.durationMinutes)}
                            </p>
                            <h4 className="text-lg font-black uppercase tracking-wide text-[#111111]">
                              Talleres simultáneos
                            </h4>
                            {formatDuration(block.durationMinutes) && (
                              <span className="text-sm text-[#737373]">
                                Duración: {formatDuration(block.durationMinutes)}
                              </span>
                            )}
                          </header>
                          <p className="mb-5 text-sm text-[#111111]/70">
                            Se imparten a la misma hora: elige uno.
                          </p>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {block.events.map((event) => (
                              <article
                                key={event.id}
                                className="programa-taller rounded-lg border border-black/10 bg-white p-5"
                              >
                                <h5 className="font-bold">{event.title}</h5>
                                {event.description && (
                                  <p className="mt-2 text-sm text-[#111111]/80">{event.description}</p>
                                )}
                                <div className="mt-3 space-y-1 text-sm">
                                  <p className="tabular-nums text-[#F04A22] font-semibold">
                                    {formatTimeRange(event.start_time, event.duration_minutes)}
                                    {formatDuration(event.duration_minutes) &&
                                      ` · ${formatDuration(event.duration_minutes)}`}
                                  </p>
                                  {event.responsible && (
                                    <p className="text-[#737373]">Responsable: {event.responsible}</p>
                                  )}
                                  {event.location && (
                                    <p className="text-[#737373]">Lugar: {event.location}</p>
                                  )}
                                </div>
                              </article>
                            ))}
                          </div>
                        </section>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Videos promocionales ── */}
      <section id="videos" className="py-24 md:py-32 bg-white px-6 md:px-12 lg:px-24" data-testid="videos-section">
        <div className="max-w-7xl mx-auto">
          <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-12">Videos Promocionales</h2>
          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, idx) => (
                <div key={video.id} data-reveal="up" style={{ '--reveal-delay': `${Math.min(idx, 5) * 80}ms` }}>
                  <YouTubeVideo
                    url={video.url}
                    title={video.title}
                    customCover={video.thumbnail || null}
                  />
                  <h3 className="mt-3 font-semibold text-lg">{video.title}</h3>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#737373]">Próximamente videos del congreso.</p>
          )}
        </div>
      </section>

      {/* ── Registro / Portal del participante ── */}
      {registrationOpen ? (
        <section id="registro" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFCFB]" data-testid="registration-section">
          <div className="max-w-3xl mx-auto">
            <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-4">Pre-Inscripción</h2>
            <p className="text-base md:text-lg leading-relaxed text-[#111111]/80 mb-12">Completa el formulario para reservar tu lugar. Te contactaremos con más detalles.</p>
            <form data-reveal="up" onSubmit={handleSubmit} className="bg-white border border-black/10 p-8 md:p-16 rounded-lg">
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">País *</Label>
                  <Select value={selectedCountry} onValueChange={handleCountryChange} required>
                    <SelectTrigger className="w-full"><SelectValue placeholder="Selecciona tu país" /></SelectTrigger>
                    <SelectContent>
                      {countryCodes.map(c => <SelectItem key={c.name} value={c.name}>{c.name} ({c.code})</SelectItem>)}
                      <SelectItem value={OTHER_COUNTRY_VALUE}>Otro país</SelectItem>
                    </SelectContent>
                  </Select>
                  {selectedCountry === OTHER_COUNTRY_VALUE && (
                    <Input
                      value={formData.country}
                      onChange={e => setFormData(p => ({ ...p, country: e.target.value }))}
                      placeholder="Escribe el nombre de tu país"
                      required
                      className="mt-3 border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]"
                    />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Código</Label>
                    <Input
                      value={phoneCode}
                      onChange={e => setPhoneCode(e.target.value)}
                      disabled={selectedCountry !== OTHER_COUNTRY_VALUE}
                      placeholder={selectedCountry === OTHER_COUNTRY_VALUE ? '+000' : ''}
                      className="bg-[#FAFAFA] border-b-2 border-t-0 border-x-0 rounded-none disabled:opacity-100"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Teléfono *</Label>
                    <Input type="tel" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} required className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Correo Electrónico *</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} required className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]" />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Instagram</Label>
                  <Input placeholder="@usuario" value={formData.instagram} onChange={e => setFormData(p => ({ ...p, instagram: e.target.value }))} className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]" />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Iglesia *</Label>
                  <Input value={formData.church} onChange={e => setFormData(p => ({ ...p, church: e.target.value }))} required className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]" />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">Ciudad *</Label>
                  <Input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} required className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]" />
                </div>

                {/* Campos dinámicos del formulario */}
                {formFields.map(field => (
                  <div key={field.id}>
                    <Label className="text-sm font-bold uppercase tracking-widest mb-2 block">
                      {field.label} {field.is_required && '*'}
                    </Label>
                    {field.field_type === 'select' ? (
                      <Select
                        value={customFields[field.field_key] || ''}
                        onValueChange={val => setCustomFields(prev => ({ ...prev, [field.field_key]: val }))}
                        required={field.is_required}
                      >
                        <SelectTrigger className="w-full"><SelectValue placeholder={field.placeholder || 'Selecciona una opción'} /></SelectTrigger>
                        <SelectContent>
                          {(field.options || []).map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.field_type === 'textarea' ? (
                      <textarea
                        value={customFields[field.field_key] || ''}
                        onChange={e => setCustomFields(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                        required={field.is_required}
                        placeholder={field.placeholder}
                        className="w-full border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22] focus:outline-none bg-transparent py-2 min-h-[80px] resize-y"
                      />
                    ) : field.field_type === 'checkbox' ? (
                      <label className="flex items-center gap-3 cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={customFields[field.field_key] || false}
                          onChange={e => setCustomFields(prev => ({ ...prev, [field.field_key]: e.target.checked }))}
                          required={field.is_required}
                          className="w-5 h-5 accent-[#F04A22]"
                        />
                        <span className="text-sm text-[#111]/70">{field.placeholder || field.label}</span>
                      </label>
                    ) : (
                      <Input
                        type={field.field_type || 'text'}
                        value={customFields[field.field_key] || ''}
                        onChange={e => setCustomFields(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                        required={field.is_required}
                        placeholder={field.placeholder}
                        className="border-b-2 border-t-0 border-x-0 rounded-none focus:border-[#F04A22]"
                      />
                    )}
                  </div>
                ))}
                <Button type="submit" disabled={submitting} className="w-full bg-[#F04A22] hover:bg-[#D03A18] text-white py-6 text-lg font-bold rounded-md">
                  {submitting ? 'Enviando...' : 'Enviar Pre-Inscripción'}
                </Button>
              </div>
            </form>
          </div>
        </section>
      ) : (
        <section id="portal" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFCFB]" data-testid="participant-portal-section">
          <div className="max-w-3xl mx-auto">
            <IdentificationBadge size={44} weight="duotone" className="text-[#F04A22] mb-6" />
            <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-4">
              {registrationSettings.closed_title || 'Portal del participante'}
            </h2>
            <p className="text-base md:text-lg leading-relaxed mb-10 max-w-2xl">
              {registrationSettings.closed_message
                || 'La pre-inscripción ha cerrado. Si ya te inscribiste, ingresa al portal para ver el programa, tus datos y descargar tu carta de invitación.'}
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              <div className="rounded-2xl bg-[#F5F5F7] p-5">
                <p className="uppercase tracking-widest text-xs font-bold mb-1">Usuario</p>
                <p className="font-semibold" style={{ color: '#1d1d1f' }}>El correo con el que te inscribiste</p>
              </div>
              <div className="rounded-2xl bg-[#F5F5F7] p-5">
                <p className="uppercase tracking-widest text-xs font-bold mb-1">Contraseña</p>
                <p className="font-semibold" style={{ color: '#1d1d1f' }}>Los últimos 8 dígitos de tu teléfono</p>
              </div>
            </div>
            <Button onClick={goToPortal} className="bg-[#F04A22] hover:bg-[#D03A18] text-white px-8 py-6 text-lg rounded-md font-bold">
              <SignIn size={22} weight="bold" className="mr-2" /> Ingresar al portal
            </Button>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      {faqs.length > 0 && (
        <section id="faq" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 data-reveal="up" className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold mb-12">Preguntas Frecuentes</h2>
            <Accordion data-reveal="up" type="single" collapsible className="border-t border-black/10">
              {faqs.map((faq, idx) => (
                <AccordionItem key={faq.id} value={`item-${idx}`}>
                  <AccordionTrigger className="text-left font-bold text-lg py-6">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-[#111111]/80 pb-6">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* ── Footer ── */}
      <footer className="bg-[#111111] text-white py-12 px-6 md:px-12 lg:px-24" data-testid="main-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            {topLogoUrl && <img src={topLogoUrl} alt="Logo" className="h-10 object-contain" />}
            {topLogo2Url && (
              <>
                <div className="w-px h-6 bg-white/20" />
                <img src={topLogo2Url} alt="Logo 2" className="h-10 object-contain" />
              </>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <ContactInYourCountry />
            <a href="/portal" className="text-sm text-white/70 hover:text-white transition-colors">Portal del participante</a>
            <a href="/admin/login" className="text-sm text-white/70 hover:text-white transition-colors">Admin Login</a>
          </div>
          <p className="text-sm text-white/70">© 2026 CNCT Congress. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
